import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccessToken } from '@features/user';
import { ArrowBack, Close, Search } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import APIService from '../../services/APIService';
import { type AvailableModule } from '../../types/types';
import ModuleConversationView from '../ModuleConversation/ModuleConversationView';

import NewModuleBlock from './NewModuleBlock';

// Types
type FilterOption = 'ALL' | 'CUSTOM' | 'JOTPSYCH';

interface ModuleSelectorProps {
  excludedModules: string[];
  onAddModules: (modules: AvailableModule[]) => void;
  onClose: () => void;
  open: boolean;
  source?: string;
  templateId?: string;
  templateName?: string;
}

interface FilterButtonProps {
  label: string;
  filterValue: FilterOption;
  currentFilter: FilterOption;
  onClick: (value: FilterOption) => void;
}

// Constants
const FILTER_OPTIONS: Record<FilterOption, string> = {
  ALL: 'All',
  CUSTOM: 'Customized by You',
  JOTPSYCH: 'Made by JotPsych',
};

// Sub-components
const FilterButton = ({ label, filterValue, currentFilter, onClick }: FilterButtonProps) => (
  <Box
    onClick={() => onClick(filterValue)}
    sx={{
      cursor: 'pointer',
      px: 2,
      py: 1,
      border: '1px solid',
      borderColor: filterValue === currentFilter ? 'primary.main' : 'divider',
      borderRadius: 2,
      bgcolor: filterValue === currentFilter ? 'rgba(100, 66, 214, 0.08)' : 'background.paper',
      color: filterValue === currentFilter ? 'primary.main' : 'text.secondary',
      fontWeight: filterValue === currentFilter ? 600 : 500,
      fontSize: '0.875rem',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        bgcolor: filterValue === currentFilter ? 'rgba(100, 66, 214, 0.12)' : 'action.hover',
        borderColor: filterValue === currentFilter ? 'primary.main' : 'text.secondary',
      },
    }}
  >
    {label}
  </Box>
);

const ModuleSelector = ({
  excludedModules,
  onAddModules,
  onClose,
  open,
  source = 'template-editor',
  templateId,
  templateName,
}: ModuleSelectorProps) => {
  const { getAccessToken } = useAccessToken();
  const eventTracking = useEventTracking();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [filteredModules, setFilteredModules] = useState<AvailableModule[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterOption, setFilterOption] = useState<FilterOption>('ALL');
  const [showCustomization, setShowCustomization] = useState(false);
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());

  // Analytics timing
  const openedAtRef = useRef<number | null>(null);
  const lastSearchRef = useRef<string>('');

  // Handlers
  const loadModules = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Authentication required');
        setIsLoading(false);
        eventTracking.error.track('Authentication required', 'module_selector');
        return;
      }

      const response = await APIService.makeAPIGetRequest({
        requestString: '/template/getAvailableModules',
        accessToken: accessToken,
      });

      if (response.ok) {
        const modules = (response.value as { modules: AvailableModule[] }).modules;
        setAvailableModules(modules);
        setError(null);
      } else {
        const errorMsg = 'Failed to load modules. Please try again.';
        setError(errorMsg);
        eventTracking.error.api('/template/getAvailableModules', 0, errorMsg);
      }
    } catch {
      const errorMsg = 'An error occurred while loading modules.';
      setError(errorMsg);
      eventTracking.error.track(errorMsg, 'module_selector');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken, eventTracking.error]);

  const handleModuleCreationComplete = () => {
    void loadModules();
    setShowCustomization(false);
    setFilterOption('CUSTOM');
  };

  // Selection handlers
  const handleToggleModule = useCallback(
    (moduleId: string) => {
      setSelectedModules(prev => {
        const next = new Set(prev);
        const isSelecting = !next.has(moduleId);

        if (isSelecting) {
          next.add(moduleId);
          const module = availableModules.find(m => m.module_id === moduleId);
          eventTracking.template.moduleSelector.sectionSelected({
            source,
            module_id: moduleId,
            selection_count: next.size,
            is_custom: module?.is_custom_user_module,
            template_id: templateId,
            template_name: templateName,
          });
        } else {
          next.delete(moduleId);
          eventTracking.template.moduleSelector.sectionDeselected({
            source,
            module_id: moduleId,
            selection_count: next.size,
            template_id: templateId,
            template_name: templateName,
          });
        }

        return next;
      });
    },
    [availableModules, eventTracking.template.moduleSelector, source, templateId, templateName],
  );

  const handleClearSelection = useCallback(() => {
    const clearedCount = selectedModules.size;
    if (clearedCount > 0) {
      eventTracking.template.moduleSelector.selectionCleared({
        source,
        cleared_count: clearedCount,
        template_id: templateId,
        template_name: templateName,
      });
    }
    setSelectedModules(new Set());
  }, [selectedModules.size, eventTracking.template.moduleSelector, source, templateId, templateName]);

  const handleAddSelected = useCallback(() => {
    const modulesToAdd = availableModules.filter(m => selectedModules.has(m.module_id));

    if (modulesToAdd.length > 0) {
      const customCount = modulesToAdd.filter(m => m.is_custom_user_module).length;
      const durationMs = openedAtRef.current ? Date.now() - openedAtRef.current : undefined;

      eventTracking.template.moduleSelector.sectionsAdded({
        source,
        section_count: modulesToAdd.length,
        custom_count: customCount > 0 ? customCount : undefined,
        duration_ms: durationMs,
        template_id: templateId,
        template_name: templateName,
      });

      onAddModules(modulesToAdd);
      setSelectedModules(new Set());
      onClose();
    }
  }, [
    selectedModules,
    availableModules,
    onAddModules,
    onClose,
    eventTracking.template.moduleSelector,
    source,
    templateId,
    templateName,
  ]);

  // Wrapped close handler for tracking
  const handleClose = useCallback(() => {
    const durationMs = openedAtRef.current ? Date.now() - openedAtRef.current : undefined;
    const hadSelection = selectedModules.size > 0;

    // Track abandonment if user had selections but didn't add them
    if (hadSelection) {
      eventTracking.template.moduleSelector.abandoned({
        source,
        had_selection: true,
        selection_count: selectedModules.size,
        duration_ms: durationMs,
        template_id: templateId,
        template_name: templateName,
      });
    }

    // Track close event
    eventTracking.template.moduleSelector.closed({
      source,
      duration_ms: durationMs,
      had_selection: hadSelection,
      template_id: templateId,
      template_name: templateName,
    });

    onClose();
    setSelectedModules(new Set()); // Clear selections when closing
  }, [selectedModules.size, eventTracking.template.moduleSelector, source, onClose, templateId, templateName]);

  // Track filter changes
  const previousFilterRef = useRef<FilterOption>('ALL');
  const handleFilterChange = useCallback(
    (newFilter: FilterOption) => {
      if (newFilter !== filterOption) {
        eventTracking.template.moduleSelector.filterChanged({
          source,
          from_filter: filterOption,
          to_filter: newFilter,
          template_id: templateId,
          template_name: templateName,
        });
        previousFilterRef.current = filterOption;
        setFilterOption(newFilter);
      }
    },
    [filterOption, eventTracking.template.moduleSelector, source, templateId, templateName],
  );

  // Track custom section flow
  const handleShowCustomization = useCallback(() => {
    eventTracking.template.moduleSelector.customSectionInitiated({
      source,
      template_id: templateId,
      template_name: templateName,
    });
    setShowCustomization(true);
  }, [eventTracking.template.moduleSelector, source, templateId, templateName]);

  const handleCancelCustomization = useCallback(() => {
    eventTracking.template.moduleSelector.customSectionCancelled({
      source,
      had_progress: undefined, // We don't track progress in conversation view yet
      template_id: templateId,
      template_name: templateName,
    });
    setShowCustomization(false);
  }, [eventTracking.template.moduleSelector, source, templateId, templateName]);

  // Track preview toggle
  const handlePreviewToggle = useCallback(
    (moduleId: string, action: 'show' | 'hide') => {
      eventTracking.template.moduleSelector.previewToggled({
        source,
        action,
        module_id: moduleId,
        template_id: templateId,
        template_name: templateName,
      });
    },
    [eventTracking.template.moduleSelector, source, templateId, templateName],
  );

  // Create tooltip content with selected section names
  const selectedSectionNames = useMemo(() => {
    if (selectedModules.size === 0) return '';

    const modulesToAdd = availableModules.filter(m => selectedModules.has(m.module_id));
    const names = modulesToAdd.map(m => m.name || m.module_name);

    const maxDisplay = 7;
    if (names.length <= maxDisplay) {
      return names.join('\n');
    }

    const displayed = names.slice(0, maxDisplay);
    const remaining = names.length - maxDisplay;
    return `${displayed.join('\n')}\n... and ${remaining} more`;
  }, [selectedModules, availableModules]);

  // Effects
  useEffect(() => {
    if (open) {
      // Reset search/filter state when modal opens
      setSearchTerm('');
      setFilterOption('ALL');
      setShowCustomization(false);
      setError(null);

      openedAtRef.current = Date.now();
      eventTracking.template.moduleSelector.opened({
        source,
        excluded_count: excludedModules.length,
        template_id: templateId,
        template_name: templateName,
      });
      loadModules();
    }
  }, [open, loadModules, eventTracking.template.moduleSelector, source, excludedModules.length, templateId, templateName]);

  // Track search with debouncing
  useEffect(() => {
    if (searchTerm && searchTerm !== lastSearchRef.current && searchTerm.length >= 2) {
      const timeoutId = setTimeout(() => {
        eventTracking.template.moduleSelector.searched({
          source,
          query_length: searchTerm.length,
          results_count: filteredModules.length,
          template_id: templateId,
          template_name: templateName,
        });
        lastSearchRef.current = searchTerm;
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, filteredModules.length, eventTracking.template.moduleSelector, source, templateId, templateName]);

  useEffect(() => {
    // Filter out excluded modules
    const availableFilteredModules = availableModules.filter(
      module => !excludedModules.includes(module.module_id),
    );

    // Apply filter based on filterOption
    const filterOptionModules = availableFilteredModules.filter(module => {
      if (filterOption === 'ALL') return module.deactivated !== true;
      if (filterOption === 'CUSTOM')
        return module.is_custom_user_module && module.deactivated !== true;
      if (filterOption === 'JOTPSYCH')
        return !module.is_custom_user_module && module.deactivated !== true;
      return true;
    });

    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();

      // Separate modules into title matches and description matches
      const titleMatches: AvailableModule[] = [];
      const descriptionMatches: AvailableModule[] = [];

      filterOptionModules.forEach(module => {
        const nameMatch = module.name?.toLowerCase().includes(searchTermLower);
        const descMatch =
          module.description?.toLowerCase().includes(searchTermLower) ||
          module.extraction_prompts?.semantic_prompt?.toLowerCase().includes(searchTermLower) ||
          module.extraction_prompts?.keyword_prompt?.toLowerCase().includes(searchTermLower);

        if (nameMatch) {
          titleMatches.push(module);
        } else if (descMatch) {
          descriptionMatches.push(module);
        }
      });

      // Sort alphabetically by name
      titleMatches.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      descriptionMatches.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      // Combine with title matches first
      setFilteredModules([...titleMatches, ...descriptionMatches]);
    } else {
      // Sort alphabetically
      const sorted = [...filterOptionModules].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      );
      setFilteredModules(sorted);
    }
  }, [availableModules, excludedModules, searchTerm, filterOption]);

  // Auto-clear selections when modules are filtered out
  useEffect(() => {
    const visibleIds = new Set(filteredModules.map(m => m.module_id));
    setSelectedModules(prev => {
      const valid = new Set([...prev].filter(id => visibleIds.has(id)));
      return valid.size !== prev.size ? valid : prev;
    });
  }, [filteredModules]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // ESC: Clear selection (Dialog already handles ESC to close)
      if (e.key === 'Escape' && selectedModules.size > 0) {
        e.preventDefault();
        e.stopPropagation();
        handleClearSelection();
      }
      // Enter: Add selected
      else if (e.key === 'Enter' && selectedModules.size > 0) {
        e.preventDefault();
        handleAddSelected();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [open, selectedModules.size, handleClearSelection, handleAddSelected]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '85vh',
          maxHeight: fullScreen ? '100%' : '85vh',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '200%',
          height: '100%',
          display: 'flex',
          transition: 'transform 0.3s ease',
          transform: showCustomization ? 'translateX(-50%)' : 'translateX(0)',
        }}
      >
        {/* Module Selector View */}
        <Box
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flex: '0 0 50%',
          }}
        >
          {/* Header with Title and Close */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={600} color="text.primary">
              Select Sections
            </Typography>
            <IconButton
              onClick={handleClose}
              size="medium"
              sx={{
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          {/* Search and Filters */}
          <Box sx={{ px: 3, pt: 3, pb: 2 }}>
            {/* Search Box */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 2,
                py: 1.5,
                bgcolor: 'background.paper',
                mb: 2,
                '&:focus-within': {
                  borderColor: 'primary.main',
                  boxShadow: '0 0 0 1px',
                  boxShadowColor: 'primary.main',
                },
              }}
            >
              <Search
                sx={{
                  color: 'text.secondary',
                  mr: 1.5,
                }}
              />
              <InputBase
                fullWidth
                placeholder="Search by section name or description"
                value={searchTerm}
                inputProps={{
                  'aria-label': 'search sections',
                }}
                onChange={e => setSearchTerm(e.target.value)}
                sx={{
                  flexGrow: 1,
                  '& input::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.8,
                  },
                }}
              />
            </Box>

            {/* Filter Options */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(Object.entries(FILTER_OPTIONS) as [FilterOption, string][]).map(
                  ([value, label]) => (
                    <FilterButton
                      key={value}
                      label={label}
                      filterValue={value}
                      currentFilter={filterOption}
                      onClick={handleFilterChange}
                    />
                  ),
                )}
              </Box>

              <Button
                variant="outlined"
                color="primary"
                size="medium"
                onClick={handleShowCustomization}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                }}
              >
                Create Custom Section
              </Button>
            </Box>
          </Box>

          {/* Content Section - Scrollable */}
          <DialogContent dividers sx={{ flexGrow: 1, px: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {filteredModules.map(module => (
                  <NewModuleBlock
                    key={module.module_id}
                    module={module}
                    isSelected={selectedModules.has(module.module_id)}
                    onToggleSelect={handleToggleModule}
                    onPreviewToggle={handlePreviewToggle}
                  />
                ))}
                {filteredModules.length === 0 && (
                  <Typography
                    variant="subtitle1"
                    align="center"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No sections found matching search criteria.
                  </Typography>
                )}
              </>
            )}
          </DialogContent>

          {/* Action Bar - Always visible */}
          {!isLoading && (
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                {selectedModules.size > 0
                  ? `${selectedModules.size} section${selectedModules.size !== 1 ? 's' : ''} selected`
                  : 'Select sections to add'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  onClick={handleClearSelection}
                  disabled={selectedModules.size === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Clear
                </Button>
                <Tooltip
                  title={selectedSectionNames}
                  placement="top"
                  arrow
                  disableHoverListener={selectedModules.size === 0}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        whiteSpace: 'pre-line',
                        maxWidth: 400,
                      },
                    },
                  }}
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddSelected}
                      disabled={selectedModules.size === 0}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        borderRadius: 2,
                      }}
                    >
                      Add {selectedModules.size > 0 ? selectedModules.size : ''} Section
                      {selectedModules.size !== 1 ? 's' : ''}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </DialogActions>
          )}
        </Box>

        {/* Module Conversation View */}
        <Box
          sx={{
            width: '50%',
            height: '100%',
            overflow: 'auto',
            flex: '0 0 50%',
            position: 'relative',
          }}
        >
          {/* Back button overlay */}
          <IconButton
            onClick={handleCancelCustomization}
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                bgcolor: 'action.hover',
                boxShadow: 2,
              },
            }}
          >
            <ArrowBack />
          </IconButton>
          <ModuleConversationView
            source="module-selector"
            onAccept={() => handleModuleCreationComplete()}
            onCancel={handleCancelCustomization}
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default ModuleSelector;
